'use client'

import {
  Box,
  Container,
  HStack,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react'
import React, { Suspense, useMemo } from 'react'
import Image from 'next/image'
import { useSelector } from 'react-redux'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'

import Logo from '../Logo'
import AppStore from '@/app/[locale]/_assets/images/app_store.png'
import GooglePlay from '@/app/[locale]/_assets/images/google_play.png'
import { IRedux, SocialMediaLinks } from '../../_store/types'
import { getPage } from '../../_utils/PagesList'
import {
  FacebookGrayModeIcon,
  InstagramGrayModeIcon,
  TelegramGrayModeIcon,
  TikTokGrayModeIcon,
  WhatsUpGrayModeIcon,
  YoutubeGrayModeIcon,
} from '../../_assets/icon-components'
import CopyrightBar from './_components/CopyrightBar'
import { formatPhoneNumber } from '../../_utils/formatPhoneNumber'
import { createUrlWithParams } from '../../_utils/createUrlWithParams'
import { useStaticPageLabels } from '../../_hooks/useStaticPageLabels'
import { getDynamicPageLabel } from '../../_utils/getDynamicPageLabel'
import { useCustomLocale } from '../../_hooks/useCustomLocale'
import { IOption } from '../../_types'

const Desktop = () => {
  const settings = useSelector((state: IRedux) => state.settings)
  const shipper = useSelector((state: IRedux) => state.session?.shipper)

  const t = useTranslations()
  const currentLocale = useCustomLocale()
  const { labelsBySlug } = useStaticPageLabels()

  const getPageLabel = (item: IOption, page: ReturnType<typeof getPage>) => {
    if (page?.type === 'dynamic') {
      return getDynamicPageLabel(item, currentLocale, labelsBySlug)
    }
    return t(page.title)
  }

  const { firstColumn, secondColumn } = useMemo(() => {
    const pages =
      settings?.data?.data?.pages?.filter(
        (item) => item?.value !== 'my_address'
      ) || []
    const midIndex = Math.ceil(pages.length / 2)

    return {
      firstColumn: pages.slice(0, midIndex),
      secondColumn: pages.slice(midIndex),
    }
  }, [settings?.data?.data?.pages])

  const social_media = settings?.data?.data?.social_media

  const socialMedias = [
    {
      id: 1,
      icon: <InstagramGrayModeIcon />,
      value: 'instagram_link',
    },
    {
      id: 2,
      icon: <FacebookGrayModeIcon />,
      value: 'facebook_link',
    },
    {
      id: 3,
      icon: <TikTokGrayModeIcon />,
      value: 'tik_tok_link',
    },
    {
      id: 4,
      icon: <WhatsUpGrayModeIcon />,
      value: 'whatsup_link',
    },
    {
      id: 5,
      icon: <YoutubeGrayModeIcon />,
      value: 'youtube_link',
    },
    {
      id: 6,
      icon: <TelegramGrayModeIcon />,
      value: 'tg_link',
    },
  ] as {
    id: number
    icon: React.ReactNode
    value: keyof SocialMediaLinks
  }[]

  const filteredSocialMedias = socialMedias
    .filter((item) => social_media?.[item.value])
    .map((item) => ({
      ...item,
      link: social_media?.[item.value],
    }))

  const formattedPhoneNumber = formatPhoneNumber(shipper?.phone?.[0] || '')

  return (
    <Box bg='background.card'>
      <Container maxW={'1280px'}>
        <SimpleGrid columns={4} gap={10} py='64px'>
          <Stack gap={4}>
            <Logo />
            {(settings?.data?.data?.app_store_link ||
              settings?.data?.data?.play_store_link) && (
              <Text fontSize='md' fontWeight={600}>
                {t('download_at')}
              </Text>
            )}
            <Stack gap={3}>
              <Suspense fallback=''>
                {settings?.data?.data?.app_store_link && (
                  <Link href={settings?.data?.data?.app_store_link}>
                    <Image
                      width={135}
                      height={40}
                      src={AppStore}
                      alt='app store'
                    />
                  </Link>
                )}
                {settings?.data?.data?.play_store_link && (
                  <Link href={settings?.data?.data?.play_store_link}>
                    <Image
                      width={135}
                      height={40}
                      src={GooglePlay}
                      alt='google play'
                    />
                  </Link>
                )}
              </Suspense>
            </Stack>
          </Stack>

          <Stack gap={3}>
            {settings?.data?.data?.is_landing && (
              <Box fontWeight={600} color='primary'>
                <Link href={createUrlWithParams('/landing-menu')}>
                  {t('menu')}
                </Link>
              </Box>
            )}
            {firstColumn.map((item) => {
              const page = getPage(item.value)
              return (
                <Box fontWeight={600} color='primary' key={item?.value}>
                  <Link href={createUrlWithParams(page.route)}>
                    {getPageLabel(item, page)}
                  </Link>
                </Box>
              )
            })}
          </Stack>

          <Stack gap={3}>
            {secondColumn.map((item) => {
              const page = getPage(item.value)
              return (
                <Box fontWeight={600} color='primary' key={item?.value}>
                  <Link href={createUrlWithParams(page.route)}>
                    {getPageLabel(item, page)}
                  </Link>
                </Box>
              )
            })}
          </Stack>

          <Stack gap={6} alignItems={'flex-end'}>
            {shipper?.phone?.[0] && (
              <Text fontWeight={700} fontSize={24} lineHeight={'24px'}>
                <Link href={`tel:${shipper?.phone?.[0]}`}>
                  {formattedPhoneNumber}
                </Link>
              </Text>
            )}
            {shipper?.work_hour_start && shipper?.work_hour_end && (
              <Text fontWeight={500} color={'text'} lineHeight={'20px'}>
                {t('from_to', {
                  from: shipper?.work_hour_start,
                  to: shipper?.work_hour_end,
                })}
              </Text>
            )}
            <HStack gap={6} color={'gray'}>
              {filteredSocialMedias?.map(
                (el) =>
                  el.link && (
                    <Link href={el.link} key={el.id} target='_blank'>
                      {el.icon}
                    </Link>
                  )
              )}
            </HStack>
          </Stack>
        </SimpleGrid>
      </Container>
      <CopyrightBar />
    </Box>
  )
}

export default Desktop
